import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { MapPin, Search, Loader2 } from 'lucide-react';
import config from '@/config/config';
import useFormStore from '@/lib/FormZustand';
import useCoordinatesStore from '@/lib/coordinates-store';
import { showOnePlz } from '@/lib/helpers/search-helpers';

// --- Constants ---
const DEBOUNCE_DELAY   = 300;  // ms
const MIN_QUERY_LENGTH = 3;
const MAX_SUGGESTIONS  = 10;
const DEFAULT_COORDS   = { lat: 50.8128, lng: 6.2160 }; // Eschweiler

// --- Debounce Hook ---
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// --- Click-Outside Hook ---
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

export default function CitySearchBox({ className = '' }) {
  const { updateFormData, formData } = useFormStore();
  const { coordinates, updateCoordinates } = useCoordinatesStore();

  const [text, setText]           = useState(formData.street || '');
  const [number, setNumber]       = useState(formData.house_number || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const debouncedText = useDebounce(text, DEBOUNCE_DELAY);
  const boxRef = useRef(null);
  useClickOutside(boxRef, () => setOpen(false));

  // --- Fetch Suggestions ---
  useEffect(() => {
    if (!open || debouncedText.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      q: debouncedText.trim(),
      limit: MAX_SUGGESTIONS.toString(),
    });
    const url = `${config.apiUrl}/api/streets?${params}`;

    fetch(url, { cache: 'default' })
      .then(r => {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then(data => setSuggestions(Array.isArray(data) ? data : []))
      .catch(() => {
        setError('Fehler beim Laden der Vorschläge.');
        setSuggestions([]);
      })
      .finally(() => setLoading(false))
  }, [debouncedText, open, coordinates]);

  // --- Handlers ---
  const onChangeText = e => {
    setText(e.target.value);
    setOpen(true);
    setError(null);
  };

  const onChangeNumber = e => {
    const val = e.target.value;
    setNumber(val);
    updateFormData(prev => ({ ...prev, house_number: val }));
  };

  const onSelect = useCallback(item => {
    const display = `${item.streetname}, ${showOnePlz(item.plz)} ${item.city}` +
      (item.ort ? ` (${item.ort})` : '');
    setText(display);
    setOpen(false);
    setError(null);

    updateCoordinates({ lat: item.lat, lng: item.lng });
    updateFormData(prev => ({
      ...prev,
      street: item.streetname,
      city: item.city,
      zip_code: item.plz || '',
      plz: item.plz || '',
      ort: item.ort || '',
      house_number: prev.house_number || ''
    }));
  }, [updateCoordinates, updateFormData]);

  const onFocus = () => {
    if (text.trim().length >= MIN_QUERY_LENGTH || suggestions.length > 0) {
      setOpen(true);
    }
  };


  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        {/* Street/City/PLZ Input */}
        <div className="relative flex-grow">
          <Label htmlFor="city-search" className="sr-only">
            Straße, Ort oder PLZ
          </Label>
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <Input
            id="city-search"
            type="text"
            value={text}
            placeholder="Straße, Ort oder PLZ…"
            onChange={onChangeText}
            onFocus={onFocus}
            autoComplete="off"
            className="w-full h-11 pl-10 pr-8 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-50"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 h-5 w-5 animate-spin text-gray-400" />
          )}
        </div>

        {/* House Number Input */}
        <div>
          <Label htmlFor="house_number" className="sr-only">
            Hausnummer
          </Label>
          <Input
            id="house_number"
            type="text"
            value={number}
            placeholder="Nr."
            onChange={onChangeNumber}
            className="w-16 h-11 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-50"
          />
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md bg-white shadow-lg border border-gray-200 max-h-60 overflow-hidden">
          {error && <div className="p-3 text-sm text-red-600">{error}</div>}
          {!loading && !error && suggestions.length === 0 && (
            <div className="p-3 text-sm text-gray-500">
              Keine Treffer gefunden.
            </div>
          )}

          {!error && (
            <ScrollArea className="h-56">
              <ul className="divide-y divide-gray-100">
                {suggestions.map((item, i) => (
                  <li key={`${item.streetname}-${item.plz}-${item.city}-${i}`}>
                    <button
                      type="button"
                      onClick={() => onSelect(item)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 flex items-center gap-3"
                    >
                      <MapPin className="h-5 w-5 text-gray-400" />
                      <span className="truncate">
                        {item.streetname}, {showOnePlz(item.plz)} {item.city}
                        {item.ort && (
                          <span className="text-gray-500"> ({item.ort})</span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}
